<script setup lang="ts">
import { computed } from 'vue';
import NxAvatar from './NxAvatar.vue';

export interface NxAvatarStackItem {
  id: string | number;
  name: string;
  highlighted?: boolean;
  /** Photo URL, see NxAvatar `src`. */
  src?: string;
}

const props = withDefaults(
  defineProps<{ users: NxAvatarStackItem[]; max?: number; size?: number }>(),
  { max: 3, size: 22 }
);

const shown = computed(() => props.users.slice(0, props.max));
const extra = computed(() => Math.max(0, props.users.length - props.max));
</script>

<template>
  <div class="nx-avatar-stack">
    <NxAvatar
      v-for="u in shown"
      :key="u.id"
      :name="u.name"
      :src="u.src"
      :size="size"
      class="nx-avatar-stack-item"
      :class="{ highlighted: u.highlighted }"
    />
    <div
      v-if="extra"
      class="nx-avatar-stack-overflow"
      :style="{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${Math.max(9, Math.round(size * 0.42))}px`,
      }"
    >
      {{ `+${extra}` }}
    </div>
  </div>
</template>

<style scoped>
/* Stack border cuts avatars out against the surrounding surface; the
   highlighted ring and overflow bubble read the same component/role tokens
   NxAvatar itself uses, so all circles in the stack stay visually uniform
   (I-596). */
.nx-avatar-stack {
  display: flex;
  align-items: center;
}

.nx-avatar-stack-item {
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-surface-bg);
  margin-left: calc(-1 * calc(var(--nxt-space-xs) * 0.75));
}

.nx-avatar-stack-item:first-child {
  margin-left: 0;
}

.nx-avatar-stack-item.highlighted {
  box-shadow: 0 0 0 var(--nxt-hairline) var(--nxt-brand-border);
}

/* The "+N" bubble reads like an NxChip: neutral fill + a visible border so it
   separates from the white page (the too-light brand tint blended in). It uses
   its own border token instead of the surface-coloured cut-out ring the avatar
   circles use. */
.nx-avatar-stack-overflow {
  border-radius: var(--nxt-radius-full);
  display: grid;
  place-items: center;
  font-weight: var(--nxt-font-weight-semibold);
  flex-shrink: 0;
  margin-left: calc(-1 * calc(var(--nxt-space-xs) * 0.75));
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-avatar-border);
  background: var(--nxt-avatar-bg);
  color: var(--nxt-avatar-fg);
}
</style>
